import React, { useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Trash2, AlertTriangle } from "lucide-react";
import { ProductoCombobox } from "@/components/ProductoCombobox";
import { PriceCombobox } from "@/components/PriceCombobox";
import { NumericInput } from "@/components/NumericInput";

/**
 * SalesLineRow
 * Fila de línea para Cotizaciones y Notas de Pedido con la misma información que Facturación:
 * - Toggle Unidad/Caja
 * - Badges (laboratorio, lote FEFO, presentación, stock, margen)
 * - Precio combobox con opciones P.S./P.M./P.Min
 * - IVA por línea
 * - Sección expandible: marca, laboratorio, presentación, registro sanitario,
 *   selector de lote (override FEFO), fechas, observación.
 * - Alerta de margen cuando precio < costo o margen real < margen aplicado.
 *
 * Props:
 *  - line, idx
 *  - productos (array)
 *  - onChange(idx, field, value) | onProductoChange(idx, productoId) | onLoteChange(idx, loteId)
 *  - onRemove(idx) | onToggleUnidad(idx, modo)
 *  - testPrefix (string) - prefix for data-testids
 *  - showIVA (bool) - show IVA select column
 */
export function SalesLineRow({
  line: l, idx: i, productos, onChange, onProductoChange, onLoteChange,
  onRemove, onToggleUnidad, testPrefix = "line", showIVA = true,
}) {
  const { canSeeCosts } = useAuth();
  const verCostos = canSeeCosts();
  const [expanded, setExpanded] = useState(false);
  const isCaja = l._unidad_venta === "caja" && l._unidades_por_caja > 1;
  const piso = isCaja ? l._precio_minimo_caja : l._precio_minimo_unidad;
  const belowMin = piso && +l.precio_unitario > 0 && +l.precio_unitario + 1e-6 < +piso;
  const opts = isCaja ? {
    precio_fijo: l._pvp_caja,
    precio_margen: l._precio_caja,
    precio_minimo: l._precio_minimo_caja,
  } : {
    precio_fijo: l._pvp_unidad,
    precio_margen: l._precio_unidad,
    precio_minimo: l._precio_minimo_unidad,
  };

  // ITER211: el margen real se muestra SIEMPRE (antes solo aparecía cuando
  // estaba por debajo del esperado, por eso "faltaba" en algunos productos).
  const margenInfo = useMemo(() => {
    const costo = parseFloat(l._costo_unidad) || 0;
    const precio = parseFloat(l.precio_unitario) || 0;
    const margenAplicado = parseFloat(l._margen_aplicado) || 0;
    if (!precio) return null;
    if (!costo) return verCostos ? { sinCosto: true } : null;
    // Comparar precio unidad equivalente vs costo unidad
    const precioUnid = isCaja && l._unidades_por_caja > 1 ? precio / l._unidades_por_caja : precio;
    const real = 1 - (costo / precioUnid);
    const bajo = !!margenAplicado && real - margenAplicado < -0.005;
    // Quien no puede ver costos solo ve la ALERTA de margen bajo
    if (!verCostos && !bajo && real >= 0) return null;
    return { real, esperado: margenAplicado || null, negativo: real < 0, bajo };
  }, [l.precio_unitario, l._costo_unidad, l._margen_aplicado, l._unidades_por_caja, l._unidad_venta, isCaja, verCostos]);

  return (
    <>
      <tr>
        <td className="text-center align-top pt-2">
          <button
            type="button"
            className="text-[#5c7368] hover:text-[#205c45]"
            onClick={() => setExpanded(v => !v)}
            title="Información del producto"
            data-testid={`${testPrefix}-expand-${i}`}
          >
            {expanded ? "▾" : "▸"}
          </button>
        </td>
        <td className="min-w-0 overflow-hidden">
          <ProductoCombobox
            value={l.producto_comercial_id}
            onChange={(v) => onProductoChange(i, v)}
            productos={productos}
            testId={`${testPrefix}-prod-${i}`}
            className="max-w-full"
          />
          {l.producto_comercial_id && (l.numero_lote || l.laboratorio_nombre || l._stock_total !== undefined) && (
            <div className="mt-1 flex gap-1 flex-wrap text-[10px]">
              {(l.laboratorio_nombre || l.laboratorio) && (
                <span className="px-1.5 py-0.5 rounded bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]">
                  {l.laboratorio_nombre || l.laboratorio}
                </span>
              )}
              {l.numero_lote && (
                <span className="px-1.5 py-0.5 rounded bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]" title="Lote FEFO sugerido">
                  ⚡ Lote {l.numero_lote}{l.fecha_vencimiento ? ` · Vence ${l.fecha_vencimiento}` : ""}
                </span>
              )}
              {l.presentacion && (
                <span className="px-1.5 py-0.5 rounded bg-white text-[#5c7368] border border-[#d1dbd6]">
                  {l.presentacion}
                </span>
              )}
              {l._stock_total !== undefined && l._stock_total !== null && (
                <span className={`px-1.5 py-0.5 rounded border ${l._stock_total > 0 ? "bg-white text-[#205c45] border-[#b2d0c2]" : "bg-[#fdecea] text-[#b53a25] border-[#d99584]"}`}>
                  Stock: {l._stock_total}
                </span>
              )}
              {l._margen_fuente && l._margen_aplicado > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-[#eef2f9] text-[#2d4a73] border border-[#b2c5db]" title="Margen aplicado">
                  {(l._margen_aplicado * 100).toFixed(0)}% · {l._margen_fuente === "producto" ? "producto" : l._margen_fuente === "tipo_cliente" ? "tipo cliente" : "default"}
                </span>
              )}
              {l._lotes_disponibles?.length > 1 && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="px-1.5 py-0.5 rounded bg-[#fef3e7] text-[#a5560c] border border-[#f0b874] hover:bg-[#fce4c1] font-semibold"
                  title="Click para ver detalle y elegir lote"
                  data-testid={`${testPrefix}-lotes-hint-${i}`}
                >
                  ⚠ {l._lotes_disponibles.length} lotes disponibles
                </button>
              )}
            </div>
          )}
          {l._lotes_disponibles?.length > 1 && (
            <div className="mt-1 text-[10px] text-[#5c7368] leading-relaxed" data-testid={`${testPrefix}-lotes-breakdown-${i}`}>
              <span className="font-semibold text-[#a5560c]">Desglose por lote:</span>{" "}
              {l._lotes_disponibles.map((lt, k) => (
                <span key={lt.id} className={`inline-block ${lt.id === l.lote_id ? "font-bold text-[#205c45] underline" : ""} mr-1.5`}>
                  {lt.numero_lote}·<span className="tabular-nums">{lt.cantidad_disponible}u</span>
                  {lt.fecha_vencimiento ? <span className="text-[#8ba397]">(v.{String(lt.fecha_vencimiento).slice(5)})</span> : null}
                  {k < l._lotes_disponibles.length - 1 ? " |" : ""}
                </span>
              ))}
            </div>
          )}
          {l._lotes_disponibles?.length > 1 && l.lote_id && (() => {
            const loteSel = l._lotes_disponibles.find(x => x.id === l.lote_id);
            const cantEntrada = parseFloat(l.cantidad) || 0;
            const disp = loteSel ? parseFloat(loteSel.cantidad_disponible) || 0 : 0;
            if (loteSel && cantEntrada > disp) {
              return (
                <div className="mt-1 text-[10px] px-1.5 py-1 rounded bg-[#fdecea] text-[#b53a25] border border-[#d99584] font-semibold" data-testid={`${testPrefix}-lote-warning-${i}`}>
                  ⚠ Cantidad ({cantEntrada}) supera lo disponible en el lote {loteSel.numero_lote} ({disp}u). Elija otro lote o reduzca la cantidad.
                </div>
              );
            }
            return null;
          })()}
        </td>
        <td>
          <div className="flex gap-1 items-center">
            <NumericInput
              className="h-9 text-right tabular-nums w-20 text-base font-semibold"
              value={l.cantidad}
              onChange={(e) => onChange(i, "cantidad", parseFloat(e.target.value) || 0)}
              data-testid={`${testPrefix}-cant-${i}`}
            />
            {l._unidades_por_caja > 1 ? (
              <div className="flex gap-0.5 text-[10px] flex-shrink-0">
                <button
                  type="button"
                  onClick={() => onToggleUnidad(i, "unidad")}
                  className={`px-1.5 py-1 rounded border ${l._unidad_venta === "unidad" ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#5c7368] border-[#d1dbd6]"}`}
                  title="Unidad"
                  data-testid={`${testPrefix}-unidad-${i}`}
                >U</button>
                <button
                  type="button"
                  onClick={() => onToggleUnidad(i, "caja")}
                  className={`px-1.5 py-1 rounded border ${l._unidad_venta === "caja" ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#5c7368] border-[#d1dbd6]"}`}
                  title="Caja"
                  data-testid={`${testPrefix}-caja-${i}`}
                >C</button>
              </div>
            ) : (
              <span className="text-[10px] text-[#8ba397]">U</span>
            )}
          </div>
          {isCaja ? (
            <div className="text-[11px] text-white font-semibold mt-1 px-1.5 py-0.5 rounded bg-[#205c45] inline-block" data-testid={`${testPrefix}-pres-${i}`}>
              📦 {l._unidades_por_caja} u/caja
            </div>
          ) : (
            <div className="text-[11px] text-[#5c7368] mt-1 px-1.5 py-0.5 rounded bg-[#eef3f1] border border-[#d1dbd6] inline-block">
              ▢ por {l.presentacion ? l.presentacion.split(/\s+/).slice(-1)[0].toLowerCase() : "unidad"}
            </div>
          )}
        </td>
        <td>
          <PriceCombobox
            value={l.precio_unitario}
            onChange={(v) => onChange(i, "precio_unitario", v)}
            options={opts}
            decimals={7}
            belowMin={belowMin}
            testId={`${testPrefix}-precio-${i}`}
          />
          {margenInfo && (
            margenInfo.sinCosto ? (
              <div
                className="mt-0.5 text-[10px] font-semibold rounded px-1 py-0.5 border flex items-center gap-1 bg-[#f7f9f8] text-[#5c7368] border-[#d1dbd6]"
                title="No se puede calcular el margen: el lote/producto no tiene costo registrado."
                data-testid={`${testPrefix}-margen-sin-costo-${i}`}
              >
                <AlertTriangle size={10} />
                <span>Sin costo · margen n/d</span>
              </div>
            ) : (
              <div
                className={`mt-0.5 text-[10px] font-semibold rounded px-1 py-0.5 border flex items-center gap-1 ${
                  margenInfo.negativo ? "bg-[#fdecea] text-[#b53a25] border-[#d99584]"
                  : margenInfo.bajo ? "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]"
                  : "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]"}`}
                title={`Margen real ${(margenInfo.real*100).toFixed(1)}%${margenInfo.esperado ? ` · esperado ${(margenInfo.esperado*100).toFixed(0)}%` : ""}${margenInfo.negativo ? " (PRECIO DEBAJO DEL COSTO)" : ""}`}
                data-testid={`${testPrefix}-margen-${i}`}
              >
                {(margenInfo.negativo || margenInfo.bajo) && <AlertTriangle size={10} />}
                <span className="tabular-nums">
                  Margen {(margenInfo.real*100).toFixed(1)}%
                  {margenInfo.bajo && ` < ${(margenInfo.esperado*100).toFixed(0)}%`}
                </span>
              </div>
            )
          )}
          {l._precios_inconsistentes && (
            <div
              className="mt-0.5 text-[10px] font-semibold rounded px-1 py-0.5 border flex items-center gap-1 bg-[#fdecea] text-[#b53a25] border-[#d99584]"
              title="Precio unidad y precio caja del maestro son inconsistentes. Ejecuta Admin → Migración → Sincronizar precio caja para reconciliar."
              data-testid={`${testPrefix}-precio-inconsistente-${i}`}
            >
              <AlertTriangle size={10} />
              <span>Precio maestro inconsistente</span>
            </div>
          )}
        </td>
        <td>
          <div className="relative">
            <NumericInput
              className="h-9 text-right tabular-nums w-full pr-6 no-spinner"
              min="0" max="100" step="0.5"
              value={l.descuento_pct ?? 0}
              onChange={(e) => onChange(i, "descuento_pct", parseFloat(e.target.value) || 0)}
              data-testid={`${testPrefix}-desc-${i}`}
            />
            <span className="absolute right-1.5 top-2 text-[10px] text-[#5c7368] font-semibold pointer-events-none">%</span>
          </div>
        </td>
        {showIVA && (
          <td>
            <Select
              value={String(l.iva_porc ?? -1)}
              onValueChange={(v) => onChange(i, "iva_porc", parseFloat(v))}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="-1">Exento</SelectItem>
                <SelectItem value="0">0%</SelectItem>
                <SelectItem value="5">5%</SelectItem>
                <SelectItem value="12">12%</SelectItem>
                <SelectItem value="15">15%</SelectItem>
              </SelectContent>
            </Select>
          </td>
        )}
        <td className="text-right font-semibold tabular-nums">${(l.subtotal || 0).toFixed(2)}</td>
        <td>
          <Button size="icon" variant="ghost" onClick={() => onRemove(i)} data-testid={`${testPrefix}-remove-${i}`}>
            <Trash2 size={14} className="text-[#b53a25]" />
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#f7f9f8]">
          <td></td>
          <td colSpan={showIVA ? 7 : 6} className="py-3">
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Marca</Label><Input className="h-8 text-xs" value={l.marca || ""} onChange={(e) => onChange(i, "marca", e.target.value)} /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Laboratorio</Label><Input className="h-8 text-xs" value={l.laboratorio_nombre || l.laboratorio || ""} onChange={(e) => onChange(i, "laboratorio_nombre", e.target.value)} /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Presentación</Label><Input className="h-8 text-xs" value={l.presentacion || ""} onChange={(e) => onChange(i, "presentacion", e.target.value)} /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Registro sanitario</Label><Input className="h-8 text-xs" value={l.registro_sanitario || ""} onChange={(e) => onChange(i, "registro_sanitario", e.target.value)} /></div>
              {l._lotes_disponibles && l._lotes_disponibles.length > 0 ? (
                <div className="col-span-2" data-testid={`${testPrefix}-lote-sel-${i}`}>
                  <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Lote a despachar · Override FEFO</Label>
                  <Select value={l.lote_id || ""} onValueChange={(v) => onLoteChange(i, v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar lote..." /></SelectTrigger>
                    <SelectContent>
                      {l._lotes_disponibles.map((lt, idxL) => (
                        <SelectItem key={lt.id} value={lt.id}>
                          {lt.numero_lote} · Vence {lt.fecha_vencimiento} · Stock {lt.cantidad_disponible}
                          {idxL === 0 ? " ⚡ FEFO" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">N° Lote</Label><Input className="h-8 text-xs" value={l.numero_lote || ""} onChange={(e) => onChange(i, "numero_lote", e.target.value)} /></div>
              )}
              <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Fecha elaboración</Label><Input className="h-8 text-xs" type="date" value={l.fecha_elaboracion || ""} onChange={(e) => onChange(i, "fecha_elaboracion", e.target.value)} /></div>
              <div><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Fecha vencimiento</Label><Input className="h-8 text-xs" type="date" value={l.fecha_vencimiento || ""} onChange={(e) => onChange(i, "fecha_vencimiento", e.target.value)} /></div>
              <div className="col-span-4"><Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Observación de línea</Label><Input className="h-8 text-xs" value={l.observacion || ""} onChange={(e) => onChange(i, "observacion", e.target.value)} placeholder="Notas internas para esta línea (no aparece en el SRI)" /></div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * SalesLineCard - VISTA MÓVIL (tarjeta apilada)
 * Misma información que SalesLineRow pero apilada verticalmente para pantallas pequeñas.
 * Diseñada para vendedores en la calle: targets grandes, scroll natural, sin truncamiento.
 *
 * Props: idénticas a SalesLineRow.
 */
export function SalesLineCard({
  line: l, idx: i, productos, onChange, onProductoChange, onLoteChange,
  onRemove, onToggleUnidad, testPrefix = "line", showIVA = true,
}) {
  const { canSeeCosts } = useAuth();
  const verCostos = canSeeCosts();
  const [expanded, setExpanded] = useState(false);
  const isCaja = l._unidad_venta === "caja" && l._unidades_por_caja > 1;
  const piso = isCaja ? l._precio_minimo_caja : l._precio_minimo_unidad;
  const belowMin = piso && +l.precio_unitario > 0 && +l.precio_unitario + 1e-6 < +piso;
  const opts = isCaja ? {
    precio_fijo: l._pvp_caja,
    precio_margen: l._precio_caja,
    precio_minimo: l._precio_minimo_caja,
  } : {
    precio_fijo: l._pvp_unidad,
    precio_margen: l._precio_unidad,
    precio_minimo: l._precio_minimo_unidad,
  };

  const margenInfo = useMemo(() => {
    const costo = parseFloat(l._costo_unidad) || 0;
    const precio = parseFloat(l.precio_unitario) || 0;
    const margenAplicado = parseFloat(l._margen_aplicado) || 0;
    if (!precio) return null;
    if (!costo) return verCostos ? { sinCosto: true } : null;
    const precioUnid = isCaja && l._unidades_por_caja > 1 ? precio / l._unidades_por_caja : precio;
    const real = 1 - (costo / precioUnid);
    const bajo = !!margenAplicado && real - margenAplicado < -0.005;
    // Quien no puede ver costos solo ve la ALERTA de margen bajo
    if (!verCostos && !bajo && real >= 0) return null;
    return { real, esperado: margenAplicado || null, negativo: real < 0, bajo };
  }, [l.precio_unitario, l._costo_unidad, l._margen_aplicado, l._unidades_por_caja, isCaja, verCostos]);

  return (
    <div
      className="rounded-lg border border-[#d1dbd6] bg-white p-3 space-y-2.5 shadow-sm"
      data-testid={`${testPrefix}-card-${i}`}
    >
      {/* Encabezado: índice + eliminar */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold tracking-wider text-[#205c45] uppercase">
          Línea {i + 1}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onRemove(i)}
          className="h-8 px-2 text-[#b53a25]"
          data-testid={`${testPrefix}-remove-${i}`}
        >
          <Trash2 size={14} className="mr-1" /> Quitar
        </Button>
      </div>

      {/* Producto */}
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Producto</Label>
        <ProductoCombobox
          value={l.producto_comercial_id}
          onChange={(v) => onProductoChange(i, v)}
          productos={productos}
          testId={`${testPrefix}-prod-${i}`}
        />
        {l.producto_comercial_id && (l.numero_lote || l.laboratorio_nombre || l._stock_total !== undefined) && (
          <div className="mt-1.5 flex gap-1 flex-wrap text-[10px]">
            {(l.laboratorio_nombre || l.laboratorio) && (
              <span className="px-1.5 py-0.5 rounded bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]">
                {l.laboratorio_nombre || l.laboratorio}
              </span>
            )}
            {l.numero_lote && (
              <span className="px-1.5 py-0.5 rounded bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]" title="Lote FEFO sugerido">
                ⚡ Lote {l.numero_lote}{l.fecha_vencimiento ? ` · ${l.fecha_vencimiento}` : ""}
              </span>
            )}
            {l.presentacion && (
              <span className="px-1.5 py-0.5 rounded bg-white text-[#5c7368] border border-[#d1dbd6]">
                {l.presentacion}
              </span>
            )}
            {l._stock_total !== undefined && l._stock_total !== null && (
              <span className={`px-1.5 py-0.5 rounded border ${l._stock_total > 0 ? "bg-white text-[#205c45] border-[#b2d0c2]" : "bg-[#fdecea] text-[#b53a25] border-[#d99584]"}`}>
                Stock: {l._stock_total}
              </span>
            )}
            {l._margen_fuente && l._margen_aplicado > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-[#eef2f9] text-[#2d4a73] border border-[#b2c5db]">
                {(l._margen_aplicado * 100).toFixed(0)}% margen
              </span>
            )}
            {l._lotes_disponibles?.length > 1 && (
              <span className="px-1.5 py-0.5 rounded bg-[#fef3e7] text-[#a5560c] border border-[#f0b874] font-semibold">
                ⚠ {l._lotes_disponibles.length} lotes disponibles
              </span>
            )}
          </div>
        )}
        {l._lotes_disponibles?.length > 1 && (
          <div className="mt-1 text-[10px] text-[#5c7368] leading-relaxed" data-testid={`${testPrefix}-lotes-breakdown-${i}`}>
            <span className="font-semibold text-[#a5560c]">Desglose:</span>{" "}
            {l._lotes_disponibles.map((lt, k) => (
              <span key={lt.id} className={`inline-block ${lt.id === l.lote_id ? "font-bold text-[#205c45] underline" : ""} mr-1.5`}>
                {lt.numero_lote}·<span className="tabular-nums">{lt.cantidad_disponible}u</span>
                {lt.fecha_vencimiento ? <span className="text-[#8ba397]">(v.{String(lt.fecha_vencimiento).slice(5)})</span> : null}
                {k < l._lotes_disponibles.length - 1 ? " |" : ""}
              </span>
            ))}
          </div>
        )}
        {l._lotes_disponibles?.length > 1 && l.lote_id && (() => {
          const loteSel = l._lotes_disponibles.find(x => x.id === l.lote_id);
          const cantEntrada = parseFloat(l.cantidad) || 0;
          const disp = loteSel ? parseFloat(loteSel.cantidad_disponible) || 0 : 0;
          if (loteSel && cantEntrada > disp) {
            return (
              <div className="mt-1 text-[10px] px-1.5 py-1 rounded bg-[#fdecea] text-[#b53a25] border border-[#d99584] font-semibold">
                ⚠ Cantidad supera lote {loteSel.numero_lote} ({disp}u)
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Cantidad + Modo Unidad/Caja (targets grandes) */}
      <div className="grid grid-cols-5 gap-2 items-end">
        <div className="col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Cantidad</Label>
          <Input
            type="number"
            inputMode="decimal"
            className="h-11 text-right tabular-nums text-base"
            value={l.cantidad}
            onChange={(e) => onChange(i, "cantidad", parseFloat(e.target.value) || 0)}
            data-testid={`${testPrefix}-cant-${i}`}
          />
        </div>
        <div className="col-span-3">
          <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Vender por</Label>
          {l._unidades_por_caja > 1 ? (
            <div className="flex gap-1.5 h-11">
              <button
                type="button"
                onClick={() => onToggleUnidad(i, "unidad")}
                className={`flex-1 rounded-md border-2 font-semibold text-sm transition-colors ${l._unidad_venta === "unidad" ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#5c7368] border-[#d1dbd6]"}`}
                data-testid={`${testPrefix}-unidad-${i}`}
              >
                Unidad
              </button>
              <button
                type="button"
                onClick={() => onToggleUnidad(i, "caja")}
                className={`flex-1 rounded-md border-2 font-semibold text-sm transition-colors ${l._unidad_venta === "caja" ? "bg-[#205c45] text-white border-[#205c45]" : "bg-white text-[#5c7368] border-[#d1dbd6]"}`}
                data-testid={`${testPrefix}-caja-${i}`}
              >
                Caja × {l._unidades_por_caja}
              </button>
            </div>
          ) : (
            <div className="h-11 flex items-center px-3 rounded-md border border-[#d1dbd6] bg-[#f7f9f8] text-sm text-[#5c7368]">
              Unidad (no permite cajas)
            </div>
          )}
        </div>
      </div>

      {/* Precio + Desc + IVA */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">
            Precio {isCaja ? "por caja" : "por unidad"}
          </Label>
          <PriceCombobox
            value={l.precio_unitario}
            onChange={(v) => onChange(i, "precio_unitario", v)}
            options={opts}
            decimals={7}
            belowMin={belowMin}
            testId={`${testPrefix}-precio-${i}`}
          />
          {margenInfo && (
            <div
              className={`mt-1 text-[10px] font-semibold rounded px-1.5 py-1 border flex items-center gap-1 ${
                margenInfo.sinCosto ? "bg-[#f7f9f8] text-[#5c7368] border-[#d1dbd6]"
                : margenInfo.negativo ? "bg-[#fdecea] text-[#b53a25] border-[#d99584]"
                : margenInfo.bajo ? "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]"
                : "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]"}`}
              data-testid={`${testPrefix}-margen-card-${i}`}
            >
              {(margenInfo.sinCosto || margenInfo.negativo || margenInfo.bajo) && <AlertTriangle size={11} />}
              <span className="tabular-nums">
                {margenInfo.sinCosto
                  ? "Sin costo · margen n/d"
                  : `Margen real ${(margenInfo.real*100).toFixed(1)}%${margenInfo.bajo ? ` (esperado ${(margenInfo.esperado*100).toFixed(0)}%)` : ""}`}
              </span>
            </div>
          )}
          {l._precios_inconsistentes && (
            <div
              className="mt-1 text-[10px] font-semibold rounded px-1.5 py-1 border flex items-center gap-1 bg-[#fdecea] text-[#b53a25] border-[#d99584]"
              title="Precio unidad y precio caja del maestro son inconsistentes. Ejecuta Admin → Migración → Sincronizar precio caja para reconciliar."
              data-testid={`${testPrefix}-precio-inconsistente-card-${i}`}
            >
              <AlertTriangle size={11} />
              <span>Precio maestro inconsistente</span>
            </div>
          )}
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Descuento</Label>
          <div className="relative">
            <Input
              type="number" inputMode="decimal" min="0" max="100" step="0.5"
              className="h-11 text-right tabular-nums pr-7 text-base no-spinner"
              value={l.descuento_pct ?? 0}
              onChange={(e) => onChange(i, "descuento_pct", parseFloat(e.target.value) || 0)}
              data-testid={`${testPrefix}-desc-${i}`}
            />
            <span className="absolute right-2.5 top-3 text-xs text-[#5c7368] font-semibold pointer-events-none">%</span>
          </div>
        </div>
        {showIVA && (
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">IVA</Label>
            <Select
              value={String(l.iva_porc ?? -1)}
              onValueChange={(v) => onChange(i, "iva_porc", parseFloat(v))}
            >
              <SelectTrigger className="h-11 text-base" data-testid={`${testPrefix}-iva-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="-1">Exento</SelectItem>
                <SelectItem value="0">0%</SelectItem>
                <SelectItem value="5">5%</SelectItem>
                <SelectItem value="12">12%</SelectItem>
                <SelectItem value="15">15%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Subtotal destacado */}
      <div className="flex items-center justify-between py-2 px-3 rounded-md bg-[#e9f2ee] border border-[#b2d0c2]">
        <span className="text-xs uppercase tracking-wider font-semibold text-[#205c45]">Subtotal</span>
        <span className="text-xl font-bold tabular-nums text-[#15241d]" data-testid={`${testPrefix}-subtotal-${i}`}>
          ${(l.subtotal || 0).toFixed(2)}
        </span>
      </div>

      {/* Detalles adicionales (colapsable) */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full text-left text-xs text-[#205c45] font-medium hover:underline flex items-center gap-1"
        data-testid={`${testPrefix}-expand-${i}`}
      >
        {expanded ? "▾ Ocultar detalles" : "▸ Más detalles (lote, fechas, observación)"}
      </button>
      {expanded && (
        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-[#d1dbd6]">
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Marca</Label>
            <Input className="h-9 text-sm" value={l.marca || ""} onChange={(e) => onChange(i, "marca", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Registro sanitario</Label>
            <Input className="h-9 text-sm" value={l.registro_sanitario || ""} onChange={(e) => onChange(i, "registro_sanitario", e.target.value)} />
          </div>
          {l._lotes_disponibles && l._lotes_disponibles.length > 0 ? (
            <div className="col-span-2" data-testid={`${testPrefix}-lote-sel-${i}`}>
              <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Lote · Override FEFO</Label>
              <Select value={l.lote_id || ""} onValueChange={(v) => onLoteChange(i, v)}>
                <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Seleccionar lote..." /></SelectTrigger>
                <SelectContent>
                  {l._lotes_disponibles.map((lt, idxL) => (
                    <SelectItem key={lt.id} value={lt.id}>
                      {lt.numero_lote} · Vence {lt.fecha_vencimiento} · Stock {lt.cantidad_disponible}
                      {idxL === 0 ? " ⚡ FEFO" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">N° Lote</Label>
              <Input className="h-9 text-sm" value={l.numero_lote || ""} onChange={(e) => onChange(i, "numero_lote", e.target.value)} />
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Elaboración</Label>
            <Input className="h-9 text-sm" type="date" value={l.fecha_elaboracion || ""} onChange={(e) => onChange(i, "fecha_elaboracion", e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Vencimiento</Label>
            <Input className="h-9 text-sm" type="date" value={l.fecha_vencimiento || ""} onChange={(e) => onChange(i, "fecha_vencimiento", e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Observación de línea</Label>
            <Input className="h-9 text-sm" value={l.observacion || ""} onChange={(e) => onChange(i, "observacion", e.target.value)} placeholder="Notas internas para esta línea" />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Hook compartido para manejar el estado de líneas con lógica idéntica a Invoicing.
 * Maneja:
 *  - addLinea, removeLinea
 *  - onProductoChange (carga info enriquecida desde endpoint)
 *  - onLoteChange (override FEFO)
 *  - toggleUnidadVenta (cambia U/C y recalcula precio)
 *  - updateLinea (recalcula subtotal con descuento %)
 *
 * @param api - instancia axios
 * @param infoUrl - función (productoId, clienteId) => endpoint URL
 * @param getClienteId - función () => string | null
 */
export function emptySalesLine() {
  return {
    _lineId: `l-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    producto_comercial_id: "",
    producto_nombre: "",
    codigo_interno: "",
    cantidad: 1,
    precio_unitario: 0,
    descuento_pct: 0,
    descuento: 0,
    subtotal: 0,
    iva_porc: -1,
    marca: "",
    laboratorio_nombre: "",
    presentacion: "",
    registro_sanitario: "",
    lote_id: null,
    numero_lote: "",
    fecha_elaboracion: "",
    fecha_vencimiento: "",
    observacion: "",
    _lotes_disponibles: [],
    _unidad_venta: "unidad",
    _unidades_por_caja: 1,
    _precio_unidad: 0,
    _precio_caja: 0,
    _pvp_unidad: 0,
    _pvp_caja: 0,
    _precio_minimo_unidad: 0,
    _precio_minimo_caja: 0,
    _costo_unidad: 0,
    _margen_aplicado: 0,
    _margen_fuente: "default",
    _stock_total: undefined,
  };
}

/**
 * Recalcula subtotal a partir de cantidad, precio_unitario y descuento_pct.
 */
export function recalcSubtotal(l) {
  const descPct = Math.min(100, Math.max(0, parseFloat(l.descuento_pct) || 0));
  const cantidad = parseFloat(l.cantidad) || 0;
  const precio = parseFloat(l.precio_unitario) || 0;
  const bruto = cantidad * precio;
  l.descuento_pct = descPct;
  l.descuento = +(bruto * descPct / 100).toFixed(4);
  l.subtotal = +Math.max(0, bruto - l.descuento).toFixed(2);
  return l;
}

/**
 * Aplica la info enriquecida del producto a la línea.
 */
export function applyProductoInfoToLine(line, data) {
  const unidadDefault = data.permite_venta_presentacion !== false && data.unidades_por_presentacion > 1 ? "caja" : "unidad";
  const precioDefault = unidadDefault === "caja" ? (data.precio_caja || 0) : (data.precio_unidad || data.precio_venta || 0);
  return {
    ...line,
    codigo_interno: data.codigo_interno || "",
    producto_nombre: data.producto_nombre || data.nombre_comercial || "",
    precio_unitario: line.precio_unitario || precioDefault,
    iva_porc: data.iva_exento ? -1 : (data.iva_porcentaje ?? 0),
    marca: data.marca || "",
    laboratorio_nombre: data.laboratorio || data.laboratorio_nombre || "",
    presentacion: data.presentacion || "",
    registro_sanitario: data.registro_sanitario || "",
    lote_id: data.lote_sugerido?.id || data.lote_id || null,
    numero_lote: data.lote_sugerido?.numero_lote || data.numero_lote || "",
    fecha_elaboracion: data.lote_sugerido?.fecha_elaboracion || data.fecha_elaboracion || "",
    fecha_vencimiento: data.lote_sugerido?.fecha_vencimiento || data.fecha_vencimiento || "",
    _lotes_disponibles: data.lotes_disponibles || [],
    // ITER179: alerta vencimiento corto (heredada del lote sugerido)
    _vencimiento_corto: !!(data.lote_sugerido?.vencimiento_corto),
    _dias_hasta_vencimiento: data.lote_sugerido?.dias_hasta_vencimiento ?? null,
    _dias_alerta_venc_corto: data.dias_alerta_vencimiento_corto ?? 180,
    _unidad_venta: unidadDefault,
    _unidades_por_caja: data.unidades_por_presentacion || 1,
    _precio_unidad: data.precio_unidad || data.precio_venta || 0,
    _precio_caja: data.precio_caja || 0,
    _pvp_unidad: data.pvp || 0,
    _pvp_caja: data.pvp_caja || 0,
    _precio_minimo_unidad: data.precio_minimo || 0,
    _precio_minimo_caja: data.precio_minimo_caja || 0,
    _costo_unidad: data.costo_unidad || 0,
    _margen_aplicado: data.margen_aplicado || 0,
    _margen_fuente: data.margen_fuente || "default",
    _stock_total: data.stock_total,
    _precios_inconsistentes: !!data.precios_inconsistentes,
  };
}

/**
 * Calcula el resumen de cajas vs unidades sueltas para mostrar en footer.
 */
export function calcResumenCaja(lineas) {
  let lineasCaja = 0, totalCajas = 0, totalUnidEquiv = 0, totalUnidades = 0;
  (lineas || []).forEach((l) => {
    const cant = +l.cantidad || 0;
    const upc = +l._unidades_por_caja || 1;
    if (l._unidad_venta === "caja" && upc > 1) {
      lineasCaja += 1;
      totalCajas += cant;
      totalUnidEquiv += cant * upc;
    } else {
      totalUnidades += cant;
    }
  });
  return { lineasCaja, totalCajas, totalUnidEquiv, totalUnidades };
}

/**
 * Componente footer "Resumen por caja".
 */
export function ResumenCajaBar({ resumen }) {
  if (!resumen || resumen.lineasCaja === 0) return null;
  return (
    <div
      className="bg-[#e9f2ee] border border-[#b2d0c2] rounded-md px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm mt-2"
      data-testid="caja-summary-bar"
    >
      <span className="text-[#205c45] font-semibold uppercase text-xs tracking-wider">📦 Resumen por caja</span>
      <span><b>{resumen.lineasCaja}</b> líneas en caja</span>
      <span><b>{resumen.totalCajas}</b> cajas totales</span>
      <span>Equivale a <b>{resumen.totalUnidEquiv.toLocaleString()}</b> unidades</span>
      {resumen.totalUnidades > 0 && <span className="text-[#5c7368]">+ {resumen.totalUnidades} unidades sueltas</span>}
    </div>
  );
}
